import { ReactNode, useState } from 'react';
import { ProductAmount, ProductData } from 'types/ProductData';
import { CarFilled } from '@ant-design/icons';
import { FarmData, FarmDeliveries, OpenHours } from '../types/FarmData';
import { safeJSONParse } from 'utils/generalHelpers';
import { IntlShape, useIntl } from 'react-intl';
import { Icon } from '../ui';
import useMarketType from './useMarketType';
import {
  calculateBasePrice,
  isRenderablePrice,
} from './productTotalPriceHelper';

interface HelperProps {
  product?: ProductData;
  productAmount?: ProductAmount;
  productHasDiscount?: boolean;
  farm?: FarmData;
  displayFlex?: boolean;
  amount?: number;
}

interface DeliveryProps {
  delivery?: FarmDeliveries;
  label?: string;
  children?: ReactNode;
  isParcel?: boolean;
  index?: number;
  intl?: IntlShape;
}

const ProductUnityPrice = ({ productAmount }: HelperProps) => {
  const packageUnitPrice = Number(productAmount?.package_unit_price);
  if (productAmount?.storage_unit === 'iep' && !!packageUnitPrice) {
    return (
      <div className="package-price d-flex flex-row">
        <div className="price">{packageUnitPrice.toFixed(2)} €</div>
        <div className="type-of-price"> /{productAmount.package_unit}</div>
      </div>
    );
  }

  return null;
};

const ProductPackageUnit = ({ productAmount }: HelperProps) => {
  const packageUnitVal = Number(productAmount?.package_unit_val);
  if (
    productAmount?.storage_unit === 'iep' &&
    !isNaN(packageUnitVal) &&
    packageUnitVal > 0
  ) {
    return (
      <span>
        {packageUnitVal.toFixed(2)}{' '}
        <span className="unit">{productAmount.package_unit}</span>
      </span>
    );
  }

  return null;
};

export const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const ProductPrice = ({
  productAmount,
  productHasDiscount,
  displayFlex,
  hideTypeOfPrice = false,
  amount = 0,
}: HelperProps & { hideTypeOfPrice?: boolean }) => {
  const intl = useIntl();
  const { isEmarket } = useMarketType();
  const { changedBasePrice, storageStockDiscountPrice } = calculateBasePrice(
    productAmount!,
    amount
  );

  let storageStockPrice = changedBasePrice;

  const hasPriceRanges = !!productAmount?.wholesale_price_ranges?.length;
  const showLowestRangePrice = hasPriceRanges && amount <= 0;

  if (showLowestRangePrice) {
    storageStockPrice = parseFloat(
      productAmount?.wholesale_price_ranges.reduce((min, item) =>
        parseFloat(item.price) < parseFloat(min.price) ? item : min
      )?.price
    );
  }

  if (isRenderablePrice(storageStockPrice)) {
    if (!productHasDiscount || !storageStockDiscountPrice) {
      return (
        <div>
          <div
            className="price-type-container d-flex flex-row"
            style={displayFlex ? { display: 'flex' } : {}}
          >
            <div className="price">
              {!isEmarket &&
                showLowestRangePrice &&
                `${intl.formatMessage({ id: 'general.from' })} `}
              {storageStockPrice.toFixed(2)} <span>€</span>
            </div>
            {!hideTypeOfPrice && (
              <div className="type-of-price">
                /{!displayFlex ? productAmount?.storage_unit : ''}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (isRenderablePrice(storageStockDiscountPrice)) {
      return (
        <div className="price-type-wrapper d-flex flex-rcol">
          <div className="price-type-second d-flex flex-row ">
            <div className="price">{storageStockPrice.toFixed(2)} €</div>
            {!hideTypeOfPrice && (
              <div className="type-of-price">
                {' '}
                /{productAmount?.storage_unit}
              </div>
            )}
          </div>
          <div className="price-grey grey-price d-flex flex-row">
            <div className="price">
              {!isEmarket &&
                showLowestRangePrice &&
                `${intl.formatMessage({ id: 'general.from' })} `}
              {storageStockDiscountPrice.toFixed(2)} <span>€</span>
            </div>
            {!hideTypeOfPrice && (
              <div className="type-of-price">
                {' '}
                /{productAmount?.storage_unit}
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  return null;
};

const DeliveryDropdown = ({
  delivery,
  label,
  children,
  isParcel,
}: DeliveryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  const ArrowDownIcon = <Icon name={'arrow-down'} type={'avarage'} />;
  const ArrowUpIcon = (
    <Icon
      name={'arrow-down'}
      type={'avarage'}
      style={{ transform: 'rotate(180deg)' }}
    />
  );

  if (!isParcel) {
    return (
      <div className={'dropdown-content'}>
        <div
          className={`dropdown-header d-flex ${isOpen ? 'active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          style={
            isOpen
              ? {
                  borderBottom: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                }
              : {}
          }
        >
          {intl.formatMessage({ id: `delivery.${label}` })}
          {isOpen ? ArrowUpIcon : ArrowDownIcon}
        </div>
        {isOpen && (
          <div
            className="dropdown-content"
            style={
              isOpen
                ? { borderTop: 'none', borderLeft: 'none', borderRight: 'none' }
                : {}
            }
          >
            <p>{delivery?.name}</p>
            <p className={'address'}>
              {delivery && `${delivery.address}, ${delivery.phone}`}
            </p>
            <table className="open-hours-table">
              <tbody>
                {delivery?.open_hours &&
                  parseOpenHours(
                    safeJSONParse<OpenHours[]>(delivery?.open_hours, [])
                  ).map((hours, index) => (
                    <tr
                      key={index}
                      className={
                        index && index % 2 === 1
                          ? 'light-gray-background'
                          : 'white-background'
                      }
                    >
                      <td className={'td-day'}>
                        {intl.formatMessage({
                          id: `${'open_hours'}.${hours.day}`,
                        })}
                      </td>
                      <td>
                        {hours.from !== 'Closed'
                          ? hours.from + ' - ' + hours.to
                          : ''}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {children}
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className={'dropdown-content'}>
        <div
          className="dropdown-header d-flex"
          onClick={() => setIsOpen(!isOpen)}
          style={
            isOpen
              ? {
                  borderBottom: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                }
              : {}
          }
        >
          {intl.formatMessage({ id: `delivery.${label}` })}
          {isOpen ? ArrowUpIcon : ArrowDownIcon}
        </div>
        {isOpen && (
          <div
            className="dropdown-content"
            style={
              isOpen
                ? { borderTop: 'none', borderLeft: 'none', borderRight: 'none' }
                : {}
            }
          >
            <p>{delivery?.type === 'delivery' && delivery?.name}</p>
            <p>
              {intl.formatMessage({ id: 'delivery.price' })}
              {delivery?.price}€
            </p>
            {children}
          </div>
        )}
      </div>
    );
  }
};

const RenderProductDelivery = ({ product }: HelperProps) => {
  const intl = useIntl();

  const farm_deliveries =
    !!product?.farm_deliveries && product.farm_deliveries.length > 0
      ? product.farm_deliveries
      : !!product?.farm?.deliveries && product.farm.deliveries.length > 0
        ? product.farm.deliveries
        : [];

  const deliveryTypes = {
    self: {
      label: 'self',
      isParcel: false,
      containerClass: 'distribution-points',
    },
    delivery: {
      label: 'delivery',
      isParcel: true,
      containerClass: 'parcel-terminals',
    },
    dpd: {
      label: 'dpd',
      isParcel: true,
      containerClass: 'parcel-terminals',
    },
    omniva: {
      label: 'omniva',
      isParcel: true,
      containerClass: 'parcel-terminals',
    },
    izipizi: {
      label: 'izipizi',
      isParcel: true,
      containerClass: 'parcel-terminals',
    },
    venipak: {
      label: 'venipak',
      isParcel: true,
      containerClass: 'parcel-terminals',
    },
  };

  if (!!farm_deliveries) {
    return (
      <>
        {farm_deliveries.map((delivery, index) => {
          const {
            label = '',
            isParcel = false,
            containerClass = '',
          } = delivery.type && deliveryTypes.hasOwnProperty(delivery.type)
            ? deliveryTypes[delivery.type as keyof typeof deliveryTypes]
            : {};

          if (!label) return null;

          const backgroundColor =
            index % 2 === 1 ? 'white-background' : 'light-gray-background';

          return (
            <div
              className={`delivery-wrapper d-flex ${backgroundColor}`}
              key={delivery.id}
            >
              <div
                className={`${containerClass} d-flex flex-row flex-gap`}
                key={delivery.id}
              >
                <DeliveryDropdown
                  key={delivery.id}
                  delivery={delivery}
                  index={index}
                  label={label}
                  isParcel={isParcel}
                />
              </div>
            </div>
          );
        })}
      </>
    );
  } else {
    return (
      <div className="delivery-wrapper d-flex white-background">
        <div className="distribution-points d-flex flex-row flex-gap">
          <div className="text-center">
            <CarFilled />
          </div>
          <div>
            <div className="label">
              {intl.formatMessage({ id: 'product_view.place_of_pickup' })}
            </div>
            <div>
              {product?.farm?.actual_full_address ??
                intl.formatMessage({
                  id: 'product_view.address_not_available',
                })}
            </div>
          </div>
        </div>
      </div>
    );
  }
};

const parseOpenHours = (openHours: OpenHours[]) => {
  try {
    const days = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];

    return days.map((day, index) => {
      const dayData = openHours[index + 1] || {};
      return {
        day,
        from: dayData['no'] || 'Closed',
        to: dayData['lidz'] || 'Closed',
      };
    });
  } catch (error) {
    console.error('Error parsing open hours:', error);
    return [];
  }
};

export {
  ProductUnityPrice,
  ProductPackageUnit,
  ProductPrice,
  RenderProductDelivery,
  DeliveryDropdown,
  parseOpenHours,
};
